import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// admin-managed 'payment_method' lookup (0037)
export type PaymentMethod = string;
// admin-managed 'payment_status' lookup (0037)
export type PaymentStatus = string;

export interface ContractorPayment {
  id: number;
  contractor_id: number;
  project_id: number | null;
  amount: string;
  payment_date: number;
  method: PaymentMethod;
  reference_number: string | null;
  notes: string | null;
  status: PaymentStatus;
  tax_year: number;
  created_at: number;
  updated_at: number;
  contractor_legal_name?: string;
  project_case_number?: string | null;
}

export interface ContractorPaymentsResponse {
  contractor_payments: ContractorPayment[];
}

export function useContractorPayments(contractorId: number | null) {
  return useQuery({
    queryKey: ['contractor-payments', { contractor_id: contractorId }],
    queryFn: ({ signal }) =>
      api.get<ContractorPaymentsResponse>(
        `/api/contractor-payments?contractor_id=${contractorId}`,
        signal,
      ),
    enabled: contractorId !== null,
  });
}

export interface CreateContractorPaymentInput {
  contractor_id: number;
  project_id?: number;
  amount: string;
  payment_date: number;
  method: PaymentMethod;
  reference_number?: string;
  notes?: string;
  status?: PaymentStatus;
}

export function useCreateContractorPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractorPaymentInput) =>
      api.post<{ contractor_payment: ContractorPayment }>('/api/contractor-payments', input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['contractor-payments', { contractor_id: vars.contractor_id }] });
    },
  });
}

export function useUpdateContractorPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: {
      id: number;
      status?: PaymentStatus;
      amount?: string;
      payment_date?: number;
      method?: PaymentMethod;
      reference_number?: string | null;
      notes?: string | null;
    }) =>
      api.patch<{ contractor_payment: ContractorPayment }>(
        `/api/contractor-payments/${id}`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractor-payments'] });
    },
  });
}
